import React from "react";
import { useI18n } from "../i18n/I18nProvider";
import { TECHNOLOGY } from "../constants/testIds";
import { Reveal } from "../components/Reveal";
import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const Connector = ({ delay = 0 }) => (
  <motion.div
    aria-hidden
    initial={{ scaleY: 0 }}
    whileInView={{ scaleY: 1 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    className="origin-top mx-auto w-px h-16 md:h-24 bg-gradient-to-b from-white/40 to-white/10"
  />
);

export const Technology = () => {
  const { t } = useI18n();
  const nodes = t.technology.nodes;

  return (
    <section
      id="technology"
      data-testid={TECHNOLOGY.section}
      className="relative bg-black py-32 md:py-44 lg:py-52 px-6 md:px-10 lg:px-14 border-t border-white/5 overflow-hidden"
    >
      {/* faint grid */}
      <div aria-hidden className="absolute inset-0 gaelon-grid-bg opacity-[0.18]" />

      <div className="relative mx-auto max-w-[1440px]">
        <Reveal>
          <div className="flex items-center gap-4 text-[10px] tracking-[0.4em] uppercase font-mono text-white/50">
            <span className="w-8 h-px bg-white/30" />
            {t.technology.eyebrow}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 font-display tracking-display text-white text-[34px] sm:text-[44px] md:text-[60px] lg:text-[72px] leading-[1.02] font-medium max-w-[900px]">
            {t.technology.title}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-[640px] text-white/60 text-[15px] leading-[1.65]">
            {t.technology.sub}
          </p>
        </Reveal>

        {/* Vertical architecture diagram */}
        <div className="mt-20 md:mt-28 flex flex-col items-center">
          {nodes.map((n, i) => (
            <React.Fragment key={n.slug}>
              <motion.div
                data-testid={TECHNOLOGY.node(n.slug)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.9, ease: EASE, delay: i * 0.05 }}
                className="relative w-full max-w-[840px]"
              >
                <div className="grid grid-cols-[80px_1fr_120px] md:grid-cols-[120px_1fr_160px] items-center gap-6 border border-white/12 bg-[#050505] hover:bg-[#0a0a0a] hover:border-white/25 transition-colors duration-500 px-5 md:px-8 py-6">
                  <span className="font-mono text-[10px] md:text-[11px] tracking-[0.32em] text-white/50">
                    L.{String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-display text-white text-[20px] md:text-[26px] tracking-tight">
                      {n.title}
                    </h3>
                    <p className="text-white/55 text-[13px] md:text-[14px] leading-[1.6] mt-1">
                      {n.desc}
                    </p>
                  </div>
                  <div className="hidden md:flex items-center justify-end gap-2 text-white/45">
                    <span className="w-2 h-2 rounded-full bg-white/70 gaelon-pulse" />
                    <span className="font-mono text-[10px] tracking-[0.3em]">ACTIVE</span>
                  </div>
                </div>
              </motion.div>
              {i < nodes.length - 1 && <Connector delay={i * 0.05 + 0.2} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Technology;
